/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect } from 'react';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useAssetsSidebarState } from '@jira/navigation-apps__sidebar-nav4-assets/src/useAssetsSidebarState.tsx';
import { AssetsUpsellButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-assets/src/ui/assets-app-item/index.tsx';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { AssetsIcon } from '@atlaskit/logo';

import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import type { AppProps } from '../types.tsx';

const lozenge = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.assets.lozenge',

	defaultMessage: 'Try',
	description: 'Growth lozenge for Assets app shortcut',
});

const assets = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.assets.assets',

	defaultMessage: 'Assets',
	description: 'Label for Assets app shortcut button',
});

const LEGACY_ASSETS_URL = '/jira/assets';

export function Assets({ onClick }: AppProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const { isUpsell, toResolvedAbsoluteUrl } = useAssetsSidebarState();
	const url = toResolvedAbsoluteUrl(LEGACY_ASSETS_URL);

	const appEditions = useAppEditions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSiteAdmin = useIsSiteAdmin();

	// Fire the upsell "viewed" event once when the shortcut first becomes visible in the upsell
	// state — not on every re-render (keyed on `isUpsell`).
	useEffect(() => {
		if (isUpsell) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'cmdbUpsellPageButton viewed', {
				serviceDeskEdition: appEditions.serviceDesk,
				isSiteAdmin,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUpsell]);

	if (isUpsell) {
		return (
			<AssetsUpsellButton
				configurableMenuId={L1_MENU_ID.GLOBAL_APP_ASSETS}
				lozengeText={formatMessage(lozenge)}
			/>
		);
	}

	return (
		<LinkMenuItem
			href={url}
			elemAfter={<LinkExternalIcon label="" spacing="spacious" size="small" />}
			elemBefore={<AssetsIcon shouldUseNewLogoDesign size="xsmall" />}
			target="_blank"
			onClick={onClick}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.assets.menu-link-item"
		>
			{formatMessage(assets)}
		</LinkMenuItem>
	);
}
